import { motion } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';

interface BlobConfig {
  color: string;
  size: number; // px
  blur: number; // px
  opacity: number;
  darkOpacity: number;
}

const RAINBOW_COLORS: BlobConfig[] = [
  { color: '#f97316', size: 260, blur: 80, opacity: 0.35, darkOpacity: 0.25 },  // orange
  { color: '#ec4899', size: 220, blur: 70, opacity: 0.30, darkOpacity: 0.22 },  // pink
  { color: '#8b5cf6', size: 300, blur: 90, opacity: 0.28, darkOpacity: 0.20 },  // purple
  { color: '#3b82f6', size: 240, blur: 75, opacity: 0.30, darkOpacity: 0.22 },  // blue
  { color: '#10b981', size: 280, blur: 85, opacity: 0.25, darkOpacity: 0.18 },  // emerald
  { color: '#eab308', size: 200, blur: 65, opacity: 0.30, darkOpacity: 0.20 },  // yellow
  { color: '#06b6d4', size: 250, blur: 80, opacity: 0.25, darkOpacity: 0.16 },  // cyan
];

function randomBetween(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

function generateRandomPosition(containerW: number, containerH: number, blobSize: number) {
  return {
    x: randomBetween(-blobSize * 0.5, containerW - blobSize * 0.5),
    y: randomBetween(-blobSize * 0.5, containerH - blobSize * 0.5),
  };
}

interface AnimatedBlobProps {
  config: BlobConfig;
  containerWidth: number;
  containerHeight: number;
  isDark: boolean;
  scale?: number;
}

function AnimatedBlob({ config, containerWidth, containerHeight, isDark, scale = 1 }: AnimatedBlobProps) {
  const actualSize = config.size * scale;
  
  const getRandomPos = useCallback(() => 
    generateRandomPosition(containerWidth, containerHeight, actualSize),
    [containerWidth, containerHeight, actualSize]
  );

  const [target, setTarget] = useState(() => getRandomPos());

  useEffect(() => {
    const interval = setInterval(() => {
      setTarget(getRandomPos());
    }, randomBetween(12000, 25000));
    return () => clearInterval(interval);
  }, [getRandomPos]);

  return (
    <motion.div
      animate={{
        x: target.x,
        y: target.y,
        scale: [1, randomBetween(0.8, 1.3), 1],
      }}
      transition={{
        duration: randomBetween(18, 35),
        ease: 'easeInOut',
        repeat: 0,
      }}
      style={{
        position: 'absolute',
        width: actualSize,
        height: actualSize,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${config.color} 0%, ${config.color}88 30%, transparent 70%)`,
        filter: `blur(${config.blur * scale}px)`,
        opacity: isDark ? config.darkOpacity : config.opacity,
        pointerEvents: 'none',
      }}
    />
  );
}

interface AnimatedBlobsProps {
  /** Which blobs to render (defaults to all 7 rainbow colors) */
  colors?: BlobConfig[];
  /** Scale factor for blob sizes (default 1) */
  scale?: number;
  /** Additional className for the container */
  className?: string;
}

export function AnimatedBlobs({ colors, scale = 1, className = '' }: AnimatedBlobsProps) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'));
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef) return;
    const update = () => {
      setDimensions({
        width: containerRef.offsetWidth,
        height: containerRef.offsetHeight,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerRef);
    return () => ro.disconnect();
  }, [containerRef]);

  const blobs = colors || RAINBOW_COLORS;

  if (dimensions.width === 0 || dimensions.height === 0) {
    return <div ref={setContainerRef} className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} />;
  }

  return (
    <div ref={setContainerRef} className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {blobs.map((blob, i) => (
        <AnimatedBlob
          key={i}
          config={blob}
          containerWidth={dimensions.width}
          containerHeight={dimensions.height}
          isDark={isDark}
          scale={scale}
        />
      ))}
    </div>
  );
}

export { RAINBOW_COLORS };
export type { BlobConfig };
